import { Avatar, Badge, Box, Button, Circle, Flex, HStack, Text, VStack } from '@bonusly/design-system';

import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';

// PLACEHOLDER (BIZY-635). Static mock entries that mimic the People Browser
// sidebar from the design so the layout/gating can be exercised before the
// real data layer + UI land. BIZY-636 replaces this with the live sidebar
// wired to `useBizyPeopleBrowserEntries`. Intentionally no i18n / analytics
// yet — this is throwaway scaffolding.

interface PlaceholderEntry {
	personId: string;
	name: string;
	jobTitle: string;
	summary: string;
	unread: boolean;
	fadingSoon: boolean;
}

const PLACEHOLDER_ENTRIES: PlaceholderEntry[] = [
	{
		personId: 'placeholder-1',
		name: 'Judith Alvarez',
		jobTitle: 'Staff Engineer',
		summary: 'Unblocked the billing migration late Friday and quietly mentored two new hires through it.',
		unread: true,
		fadingSoon: false,
	},
	{
		personId: 'placeholder-2',
		name: 'Marcus Lee',
		jobTitle: 'Product Designer',
		summary: 'Reframed the onboarding flow in review and saved the team a week of rework.',
		unread: true,
		fadingSoon: true,
	},
	{
		personId: 'placeholder-3',
		name: 'Priya Nair',
		jobTitle: 'Support Lead',
		summary: 'Turned a rough customer escalation into a five-star renewal this week.',
		unread: false,
		fadingSoon: false,
	},
];

export function BizyPeopleBrowser() {
	const focusedPersonId = useBizySidePanelStore((state) => state.focusedPersonId);

	return (
		<VStack data-component="bizy-people-browser" align="stretch" spacing={0} h="100%" overflowY="auto" p={3}>
			<Text fontSize="sm" fontWeight="semibold" color="text.muted" px={1} pb={2}>
				People
			</Text>
			<VStack align="stretch" spacing={2}>
				{PLACEHOLDER_ENTRIES.map((entry) => {
					const isFocused = focusedPersonId === entry.personId;
					return (
						<Box
							key={entry.personId}
							borderWidth="1px"
							borderColor={isFocused ? 'border.emphasis' : 'border.soft'}
							bg={isFocused ? 'bg.muted' : 'bg.surface'}
							borderRadius="md"
							p={3}
						>
							<HStack align="flex-start" spacing={3}>
								<Box position="relative" flexShrink={0}>
									<Avatar name={entry.name} size="sm" />
									{entry.unread && (
										<Circle size="10px" bg="accent.default" position="absolute" top="-1px" right="-1px" borderWidth="2px" borderColor="bg.surface" />
									)}
								</Box>
								<VStack align="stretch" spacing={1} minW={0} flex={1}>
									<HStack justify="space-between" align="center" spacing={2}>
										<Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
											{entry.name}
										</Text>
										{entry.fadingSoon && (
											<Badge colorScheme="orange" fontSize="2xs" flexShrink={0}>
												Fading soon
											</Badge>
										)}
									</HStack>
									<Text fontSize="xs" color="text.muted" noOfLines={1}>
										{entry.jobTitle}
									</Text>
									<Text fontSize="xs" color="text.default" noOfLines={2}>
										{entry.summary}
									</Text>
									<HStack pt={1} spacing={2}>
										<Button size="xs" variant="solid" colorScheme="primary">
											Let&apos;s dive in
										</Button>
										<Button size="xs" variant="ghost" colorScheme="secondary">
											Skip
										</Button>
									</HStack>
								</VStack>
							</HStack>
						</Box>
					);
				})}
			</VStack>
			<Flex pt={3} px={1}>
				<Text fontSize="2xs" color="text.subtle">
					Placeholder — People Browser (BIZY-635)
				</Text>
			</Flex>
		</VStack>
	);
}
